import React, { useEffect, useMemo, useState } from 'react';
import { Activity, Search, Loader2, Trash2, RotateCcw, Download } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '../components/common';
import { useI18n } from '../i18n/I18nContext';
import { useRealtime } from '../realtime/RealtimeContext';
import { exportCSV } from '../utils/exportCSV';
import { shiftForDate } from '../utils/shifts';
import { formatDateTime } from '../utils/prefs';
import api from '../api/client';

const EVENT_META = {
  STARTED: 's-running', STOPPED: 's-stopped', PROBLEM_REPORTED: 's-problem',
  BREAK_STARTED: 's-break', BREAK_ENDED: 's-break', JOB_ASSIGNED: 's-progress', STATUS_CHANGED: 's-planned',
};

export default function MachineLogs() {
  const { t } = useI18n();
  const [logs, setLogs] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [evt, setEvt] = useState('ALL');

  const load = () => {
    api.listLogs().then(setLogs).catch(() => {}).finally(() => setLoading(false));
    api.listAssignments().then(setAssignments).catch(() => {});
    api.getShifts().then(setShifts).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  useRealtime(() => load());

  const shiftOf = (l) => shiftForDate(shifts, new Date(l.ts || l.time));

  // Live machine -> assigned operator map from Operator Assignments.
  // Prefer an ON_DUTY assignment so duplicates resolve to the active operator.
  const machineOperator = useMemo(() => {
    const map = {};
    const onDuty = assignments.filter((a) => a.status === 'ON_DUTY');
    const offDuty = assignments.filter((a) => a.status !== 'ON_DUTY');
    offDuty.forEach((a) => (a.machines || []).forEach((mid) => { map[mid] = a.operator; }));
    onDuty.forEach((a) => (a.machines || []).forEach((mid) => { map[mid] = a.operator; }));
    return map;
  }, [assignments]);

  const opFor = (l) => machineOperator[l.machine] || l.operator || 'System';

  const events = Array.from(new Set(logs.map((l) => l.event)));

  const rows = useMemo(() => logs.filter((l) => {
    const q = query.toLowerCase();
    const mq = !q || l.machine.toLowerCase().includes(q) || (l.machineName || '').toLowerCase().includes(q) || opFor(l).toLowerCase().includes(q);
    const me = evt === 'ALL' || l.event === evt;
    return mq && me;
  }), [logs, query, evt, machineOperator]);

  const removeLog = async (l) => {
    setLogs((prev) => prev.filter((x) => x.id !== l.id));
    try { await api.deleteLog(l.id); toast.success(t('logs.deleted')); }
    catch (e) { toast.error(t('common.deleteFailed')); load(); }
  };

  const resetAll = async () => {
    if (!window.confirm(t('logs.resetConfirm'))) return;
    try {
      const res = await api.resetLogs();
      setLogs([]);
      if (res.undo_token) {
        toast.success(t('logs.allReset'), {
          duration: 60000,
          action: {
            label: t('common.undo'),
            onClick: async () => {
              try { await api.restoreLogs(res.undo_token); toast.success(t('logs.restored')); load(); }
              catch (e) { toast.error(e?.response?.data?.detail || t('common.undoFailed')); }
            },
          },
        });
      } else {
        toast.success(t('logs.noReset'));
      }
    } catch (e) { toast.error(t('common.resetFailed')); load(); }
  };

  const handleExport = () => {
    exportCSV('kmt_machine_logs.csv', [
      { label: 'Time', get: (r) => (r.ts ? formatDateTime(r.ts) : r.time) },
      { label: 'Machine', get: (r) => r.machine },
      { label: 'Machine Name', get: (r) => r.machineName },
      { label: 'Event', get: (r) => r.event },
      { label: 'Shift', get: (r) => shiftOf(r) },
      { label: 'Operator', get: (r) => opFor(r) },
      { label: 'Detail', get: (r) => r.detail },
    ], rows);
    toast.success(t('logs.exported'));
  };

  return (
    <div className="kmt-fade-in">
      <PageHeader icon={Activity} title={t('nav.logs')} subtitle={`${logs.length} ${t('logs.sub')}`}
        right={<>
          <button className="kmt-btn-ghost flex items-center gap-2" onClick={handleExport} data-testid="logs-export-csv-btn"><Download size={16} />{t('common.exportCsv')}</button>
          <button className="kmt-btn-ghost flex items-center gap-2" onClick={resetAll} data-testid="logs-reset-all-btn" style={{ color: 'var(--kmt-red)' }}><RotateCcw size={15} />{t('common.resetAll')}</button>
        </>} />
      <div className="p-8 space-y-5">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2" style={{ color: 'var(--kmt-muted-2)' }} />
            <input className="kmt-input" style={{ paddingLeft: 40 }} placeholder={t('common.search') + '…'} value={query} onChange={(e) => setQuery(e.target.value)} data-testid="logs-search-input" />
          </div>
          <select className="kmt-input md:w-56" value={evt} onChange={(e) => setEvt(e.target.value)} data-testid="logs-event-filter">
            <option value="ALL">{t('logs.allEvents')}</option>
            {events.map((e) => <option key={e} value={e}>{e.replace(/_/g, ' ')}</option>)}
          </select>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-24" style={{ color: 'var(--kmt-muted)' }}><Loader2 className="animate-spin" size={28} /></div>
        ) : rows.length === 0 ? (
          <div className="kmt-panel py-16 text-center" style={{ color: 'var(--kmt-muted)' }}>{t('logs.empty')}</div>
        ) : (
          <div className="kmt-panel overflow-x-auto">
            <table className="w-full kmt-table">
              <thead><tr>
                <th className="kmt-th">{t('col.time')}</th><th className="kmt-th">{t('col.machine')}</th><th className="kmt-th">{t('col.event')}</th><th className="kmt-th">{t('col.shift')}</th><th className="kmt-th">{t('col.operator')}</th><th className="kmt-th">{t('col.detail')}</th><th className="kmt-th">{t('col.action')}</th>
              </tr></thead>
              <tbody>
                {rows.map((l) => (
                  <tr key={l.id} data-testid={`log-row-${l.id}`}>
                    <td className="kmt-td font-mono-k" data-label="Time" style={{ color: 'var(--kmt-muted)' }}>{l.ts ? formatDateTime(l.ts) : l.time}</td>
                    <td className="kmt-td" data-label="Machine"><span className="font-mono-k" style={{ color: 'var(--kmt-gold)' }}>{l.machine}</span> <span style={{ color: 'var(--kmt-muted)' }}>{l.machineName}</span></td>
                    <td className="kmt-td" data-label="Event"><span className={`kmt-badge ${EVENT_META[l.event] || 's-planned'}`}>{l.event.replace(/_/g, ' ')}</span></td>
                    <td className="kmt-td" data-label="Shift" style={{ color: 'var(--kmt-muted)' }}>{shiftOf(l)}</td>
                    <td className="kmt-td" data-label="Operator">{opFor(l)}</td>
                    <td className="kmt-td" data-label="Detail" style={{ color: 'var(--kmt-muted)' }}>{l.detail}</td>
                    <td className="kmt-td" data-label="Action">
                      <button className="kmt-btn-ghost text-xs flex items-center gap-1" onClick={() => removeLog(l)} data-testid={`log-delete-${l.id}`} style={{ color: 'var(--kmt-red)' }}><Trash2 size={13} />{t('common.delete')}</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
